/* eslint-disable @typescript-eslint/no-non-null-assertion */
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import { InviteeResponse, ProfileReuseStatus } from 'api';
import { TextV2 } from '@insights-ltd/design-library/components';
import { formatDate } from '@insights-ltd/design-library/utils';
import { useTranslation } from 'react-i18next';
import ButtonBase from '@mui/material/ButtonBase';
import { CHAPTERS } from 'variables';
import { Link, styled } from '@mui/material';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { formatList } from 'utils/formatList';

type ActionRequiredListRowProps = {
  invitee: InviteeResponse;
  updateInvitee: (
    profileReusePreference: ProfileReuseStatus,
    invitee: InviteeResponse,
  ) => Promise<void>;
  deleteInvitee: (inviteeId: string) => void;
  isLastRow: boolean;
  isPastExperience: boolean;
  eventTimezone: string;
  eventDialect: string;
};

const ReuseContainer = styled('div')({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
});

const StyledDecisionsButton = styled(TextV2)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: `0 ${theme.spacing(spacingSizeMap.XS)} 0 ${theme.spacing(
    spacingSizeMap.XS,
  )}`,
  fontWeight: theme.typography.fontWeightBold,
}));

const ActionRequiredListRow = ({
  updateInvitee,
  deleteInvitee,
  invitee,
  isLastRow,
  isPastExperience,
  eventTimezone,
  eventDialect,
}: ActionRequiredListRowProps) => {
  const { t, i18n } = useTranslation();
  const handleDeleteInvites = () => deleteInvitee(invitee.id);
  const chapters = formatList(
    (invitee.chapters ?? [])
      .map((chapter) => CHAPTERS.find((el) => el.id === chapter.name)?.titleKey)
      .filter((chapterKey) => chapterKey !== undefined)
      .map((chapterKey) => t(chapterKey)),
    i18n.language,
  );

  const yesOnClick = () => updateInvitee('REQUEST', invitee);
  const noOnClick = () => updateInvitee('DECLINE', invitee);

  return (
    <>
      <Grid
        container
        spacing={1}
        sx={[
          {
            alignItems: 'flex-start',
          },
          (theme) => ({
            color: theme.palette.grey[700],
            wordBreak: 'break-word',
          }),
        ]}
      >
        <Grid size={3}>
          <TextV2>{invitee.fullName}</TextV2>
          <TextV2>
            <Link
              target="_blank"
              href={`mailto:${invitee.email}`}
              rel="noopener noreferrer"
            >
              {invitee.email}
            </Link>
          </TextV2>
        </Grid>
        <Grid size={4}>
          <TextV2>
            {t('ui.event-management.events.invitee.existing-profile', {
              profileDate: formatDate(
                invitee.profile!.createdAt!,
                eventTimezone,
                eventDialect,
              ),
            })}
          </TextV2>
          <TextV2>{chapters}</TextV2>
        </Grid>
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            textAlign: 'right',
          }}
          size={3}
        >
          <ReuseContainer>
            <TextV2>
              {t('ui.event-management.events.invitee.profile-reuse-question')}
            </TextV2>
            <ButtonBase
              disableTouchRipple
              sx={(theme) => ({
                color: theme.palette.primary.main,
                fontWeight: theme.typography.fontWeightBold,
                '&:hover, &:focus': {
                  textDecoration: 'underline',
                },
              })}
              onClick={yesOnClick}
            >
              <StyledDecisionsButton color="primary">
                {t('ui.event-management.events.invitee.profile-reuse-yes')}
              </StyledDecisionsButton>
            </ButtonBase>
            <TextV2 component="span">
              {t('ui.event-management.events.invitee.profile-reuse-slash')}
            </TextV2>
            <ButtonBase
              disableTouchRipple
              sx={(theme) => ({
                color: theme.palette.error.main,
                fontWeight: theme.typography.fontWeightBold,
                '&:hover, &:focus': {
                  textDecoration: 'underline',
                },
              })}
              onClick={noOnClick}
            >
              <StyledDecisionsButton color="error">
                {t('ui.event-management.events.invitee.profile-reuse-no')}
              </StyledDecisionsButton>
            </ButtonBase>
          </ReuseContainer>
        </Grid>
        <Grid
          container
          direction="row"
          size={2}
          sx={{
            justifyContent: 'flex-end',
            textAlign: 'right',
          }}
        >
          {!isPastExperience && (
            <Button
              onClick={handleDeleteInvites}
              aria-label={t(
                'ui.event-management.events.invitee.confirm-delete.title',
              )}
              sx={{ minWidth: 0, padding: 0, height: '1.75rem' }}
            >
              <TextV2 variant="bodyBold" color="error">
                {t('ui.event-management.invite-list.remove-user')}
              </TextV2>
            </Button>
          )}
        </Grid>
      </Grid>
      {!isLastRow && <Divider variant="fullWidth" />}
    </>
  );
};

export default ActionRequiredListRow;
/* eslint-enable @typescript-eslint/no-non-null-assertion */
